import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Drawer, Form, Modal, Select, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { getInstitutions } from "../../services/institutionService";
import DateInput from "../../components/Inputs/DateInput";
import {
  createMilestone,
  deleteMilestone,
  getMilestones,
  updateMilestone,
} from "../../services/milestoneService";
import { milestoneSchema } from "../../validators/milestoneValidators";
import TextArea from "../../components/Inputs/TextAreaInput";
import { format } from "date-fns";
import { BiShow } from "react-icons/bi";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const Milestone = () => {
  const [milestones, setMilestones] = useState([]);
  const [filteredMilestones, setFilteredMilestones] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [milestoneToEdit, setMilestoneToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [milestoneToDelete, setMilestoneToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadMilestones();
    loadInstitutions();
  }, []);

  useEffect(() => {
    let filtered = milestones;
    if (searchTerm.trim() !== "") {
      filtered = milestones.filter((item) =>
        item?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    setFilteredMilestones(filtered);
  }, [searchTerm, milestones, selectedInstitution]);

  const loadInstitutions = async () => {
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    }
  };

  const loadMilestones = async () => {
    setLoading(true);
    try {
      const { data } = await getMilestones();

      setMilestones(data.data);
      setFilteredMilestones(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load milestones");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (milestoneToEdit) {
      onUpdateMilestone(payload, resetForm);
    } else {
      onCreateMilestone(payload, resetForm);
    }
  };

  const onCreateMilestone = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createMilestone(payload);

      setMilestones((prev) => [...prev, data.data]);

      toast.success("Milestone added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Milestone was not added, try again!"
        );
      }
    }
  };

  const handleEdit = (category) => {
    setMilestoneToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateMilestone = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalMilestones = [...milestones];

      const milestoneIndex = originalMilestones.findIndex(
        (milestone) => milestone.id === milestoneToEdit.id
      );

      if (milestoneIndex === -1) throw new Error("Milestones not found.");

      const { data } = await updateMilestone(milestoneToEdit.id, payload);

      originalMilestones[milestoneIndex] = data.data;

      setMilestones(originalMilestones);

      setMilestoneToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Milestone updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Milestone not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setMilestoneToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteMilestone = async () => {
    const originalMilestones = [...milestones];

    const newMilestones = originalMilestones.filter(
      (milestone) => milestone.id !== milestoneToDelete.id
    );

    setMilestones(newMilestones);

    try {
      await deleteMilestone(milestoneToDelete?.id);

      toast.success("Milestone deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Milestone not deleted, try again later"
        }`
      );

      setMilestones(originalMilestones);
    } finally {
      setIsDeleteModalOpen(false);

      setMilestoneToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setMilestoneToEdit(null);
    setMilestoneToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Milestone",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "center", color: "#888" }}>
            {displayText}
          </div>
        ) : (
          <>{displayText}</>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (dateStr) => {
        if (!dateStr) return "N/A";

        try {
          return format(new Date(dateStr), "do MMMM yyyy");
        } catch {
          return "Invalid date";
        }
      },
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Milestone">
            <button
              className="p-2 border border-blue-200 rounded-md text-blue-600 hover:bg-blue-50 transition"
              onClick={() => {
                setSelectedMilestone(record);
                setIsDrawerOpen(true);
              }}
            >
              <BiShow size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Edit Milestone">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Milestone">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Milestones" subtitle1="Milestones" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Milestones"
            count={filteredMilestones.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Milestone"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredMilestones.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              !isInstAdmin && (
                <div className="mb-2 py-2 flex gap-2">
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => {
                      setSelectedInstitution(value);
                    }}
                    showSearch={true}
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Select.Option value={"Not Selected"}>
                      Filter by Institution
                    </Select.Option>
                    {institutions &&
                      institutions.length > 0 &&
                      institutions.map((option) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                </div>
              )
            }
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {milestoneToEdit ? "Edit Milestone" : "Add Milestone"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: milestoneToEdit ? milestoneToEdit?.name : "",
                description: milestoneToEdit
                  ? milestoneToEdit?.description
                  : "",
                date: milestoneToEdit ? milestoneToEdit?.date : "",
                institution_id: milestoneToEdit
                  ? milestoneToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
              }}
              enableReinitialize
              validationSchema={milestoneSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                setFieldTouched,
                submitForm,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          milestoneToEdit
                            ? milestoneToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}

                    <TextInput
                      name="name"
                      title="Milestone"
                      placeholder="e.g. Completed Prototype Development"
                      requiredField
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextArea
                      name="description"
                      title="Description"
                      placeholder="e.g. Successfully completed the initial prototype and conducted lab testing for the energy-efficient water purification unit."
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <DateInput
                      name="date"
                      title="Milestone Date "
                      placeholder="Select milestone date"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteMilestone}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />

          <Drawer
            title={
              <div className="flex flex-col gap-1">
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedMilestone?.name || "Milestone Details"}
                </h3>
                <p className="text-sm text-gray-500">
                  {selectedMilestone?.institution?.name ||
                    "Institution Unknown"}
                </p>
              </div>
            }
            width={520}
            onClose={() => setIsDrawerOpen(false)}
            open={isDrawerOpen}
            placement="right"
          >
            {selectedMilestone ? (
              <div className="space-y-4 text-sm text-gray-700">
                <p>
                  <strong>Date:</strong>{" "}
                  {selectedMilestone.date
                    ? format(new Date(selectedMilestone.date), "do MMMM yyyy")
                    : "N/A"}
                </p>

                <p>
                  <strong>Description:</strong>
                </p>
                <p className="text-gray-600 whitespace-pre-wrap">
                  {selectedMilestone.description || "No description provided."}
                </p>
              </div>
            ) : (
              <p className="text-gray-400">No milestone selected.</p>
            )}
          </Drawer>
        </div>
      </div>
    </>
  );
};

export default Milestone;
